import { useEffect } from "react";
import Modal from "@components/Modal";
import { useModal } from "@components/Modal/ModalProvider";
import EjectionFractionModalContent from "./EjectionFractionModalContent";
import { Series } from "@models/Series";
import { useBufferPool } from "@features/analysis/rendering/Panels/Renderers/vtkManagers";
import { useBrowserPage } from "@features/browser/useBrowserPage";
import { useConfig } from "config/useConfig";
import { CalciumAnalysisResult } from "@models/Parameter";
import { fetchSeriesTargetEx } from '@api/files';
import { parse_readNiftiFile } from '../../../../../main/NIfTIReader';
import useAuth from "@hooks/Auth/useAuth";
import { useAnalysisPage } from "@features/analysis/useAnalysisPage";

export function useEjectionFractionModal(){
  const { openModal, closeModal } = useModal();
  const {bufferPool,setBufferPool} = useBufferPool();
  const { selectedSeries, selectedSeriesId, updateSeriesSections, seriesSelections } = useBrowserPage();
  const { setData } = useConfig();
  const { accessToken } = useAuth();
  const { updateSelectAnalysis } = useAnalysisPage();
  const pool = bufferPool[selectedSeriesId!];
  const paramData = (pool?.parameter?.data ?? {}) as CalciumAnalysisResult;

  useEffect(() => {
    updateSeriesSections([]);
  }, [selectedSeriesId]);

  const makeReport = (values: number[], chamber:{ id: string; value: number }[]) => ({
    ejectionFraction: values[0].toFixed(2) + "%",
    endDiastolicVolume: values[1].toFixed(2) + " ml",
    endSystolicVolume: values[2].toFixed(2) + " ml",
    strokeVolume: values[3].toFixed(2) + " ml",
    endDiastolicMyocardialVolume: values[4].toFixed(2) + " ml",
    endDiastolicMyocardialMass: values[5].toFixed(2) + " g",
    chamber,
  });

  function findMinMaxByChamber<T extends Record<string, { Chamber: number; Wall: number }>>(
    data: T,
    excludeKeys: string[] = ['Report']
  ) {
    return Object.entries(data)
      .filter(([key]) => !excludeKeys.includes(key))
      .reduce(
        (acc, [key, value]) => {
          if (value.Chamber < acc.minVal) {
            acc.minVal = value.Chamber;
            acc.minKey = key;
          }
          if (value.Chamber > acc.maxVal) {
            acc.maxVal = value.Chamber;
            acc.maxKey = key;
          }
          acc.chamber.push({ id: key, value: value.Chamber });
          return acc;
        },
        {
          minKey: null as string | null,
          maxKey: null as string | null,
          minVal: Infinity, maxVal: -Infinity,
          chamber: [] as { id: string; value: number }[],
        }
      );
  }




  const initEF = async () => {
    if(!paramData || !paramData?.SavedInfo) return;
    const ejectionFraction = paramData?.SavedInfo?.EjectionFraction;
    const ef_result_info = ejectionFraction.Info;
    const ef_value = ejectionFraction.data;
    const data: any = Object.entries(ef_result_info).map(([key, val]) => [
      key, val.Series,
    ]);
    if(seriesSelections.length === 0){
      const select: { id: string; percent: string }[] = [];
      for (let i = 0; i < data.length; i++) {
        const [percent, id] = data[i];
        select.push({ id, percent: percent });
      }
      updateSeriesSections(select);
    }
    await handleEFComplete?.(ef_result_info,ef_value,data);
  }

  const handleEFComplete = async (ef_result_info: any, ef_value: any, data: string[][]) => {
    if(!selectedSeriesId) return;
    const select: {id:string; percent:string;}[] = [];

    const parsedArray = await Promise.all(
      Object.entries(ef_result_info).map(async ([percent, cur_path]) => {
        const file = String(cur_path["File"]);
        const chamber_angle = cur_path["Chamber4"];
        const Series = cur_path["Series"];
        const { data, status } = await fetchSeriesTargetEx({
          accessToken,
          seriesId: selectedSeriesId!,
          target: file,
          res: { Pragma: 'no-cache', Expires: '0' },
        });
        if (status === 200 || status === 201) {
          const res = parse_readNiftiFile(data);
          select.push({ id:Series, percent: percent });
          return {
            percent: Number(percent),
            image: new Uint8Array(res.image),
            chamberInfo:{
              normal :[chamber_angle[0],chamber_angle[1],-chamber_angle[2]] as  [number,number,number],
              right : [chamber_angle[6],chamber_angle[7],-chamber_angle[8]] as [number,number,number],
              up : [chamber_angle[9],chamber_angle[10],-chamber_angle[11]] as [number,number,number],
              center : [-chamber_angle[3],-chamber_angle[4],(chamber_angle[5])]  as [number,number,number]
            }
          };
        }
        return null;
      })
    );
    const parsed_data_list = parsedArray.filter(Boolean) as { percent: number; image: Uint8Array, chamberInfo:any }[];
    const parsedData = parsed_data_list.map(item => {
      const match = data.find(([p]) => Number(p) === item.percent)!;
      return {
        ...item,
        id: match[1],
      };
    });
    console.log("parsed_data:", parsedData, ef_value);
    console.log("new select:", select, seriesSelections);
    updateSeriesSections(select);

    const buffers = bufferPool[selectedSeriesId];
    if (!buffers) return;

    buffers['EF'].data = {};
    buffers['EF'].loaded = true;

    const efBufferData = buffers['EF'].data as Record<
      string,
      { percent: number; data: Uint8Array; chamberInfo: any }
    >;

    const percents = parsedData.map(({ id, percent }) => ({
      id,
      value: percent,
    }));

    parsedData.forEach(({ percent, image, id, chamberInfo }) => {
      efBufferData[id] = {
        percent,
        data: image instanceof Uint8Array ? image : new Uint8Array(image),
        chamberInfo,
      };
    });

    const lvChamber = findMinMaxByChamber(ef_value.LV);
    const rvChamber = findMinMaxByChamber(ef_value.RV);

    setData((prev) => ({
      reportEF: {
        ...prev.reportEF,
        percents,
        maxChamberLV: lvChamber.maxKey ?? '',
        minChamberLV: lvChamber.minKey ?? '',
        LV: makeReport(ef_value.LV.Report, lvChamber.chamber),
        RV: makeReport(ef_value.RV.Report, rvChamber.chamber),
      },
    }));

    setBufferPool((prev) => ({
      ...prev,
      [selectedSeriesId]: { ...prev[selectedSeriesId], ['EF']: buffers['EF'] },
    }));

    updateSelectAnalysis(selectedSeries!, 'ejectionFraction');
  };


  const EjectionFractionModalOpen = (
    series: Series,
    calculateEF = false,
    segmentationStatusBySeries?: Record<string, boolean>,
  ) => {
    if(calculateEF && paramData) {
      const hasEjectionFraction = !!paramData.SavedInfo?.EjectionFraction;
      if(hasEjectionFraction){
        initEF();
        return;
      }
    };
    const id = openModal(
      <Modal
        type="page"
        close={() => {closeModal(id)}}
        description={
          <EjectionFractionModalContent
            close={() => closeModal(id)}
            series={series}
            onComplete ={handleEFComplete}
            segmentationStatusBySeries={segmentationStatusBySeries}
          />}
      />,
      false,
      () => closeModal(id)
    )
  }

  return EjectionFractionModalOpen;
}
